/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { useLazyQuery, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons"; // Npm: Chakra UI icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI DataGrid.
import { HiArrowPath, HiArrowUturnDown } from "react-icons/hi2"; // Npm: react-icons library.
import {
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import CustomerFinancialQuery from "./__query__/index.customer.read.query";
import CustomerMarginReportQuery from "./__query__/index.customer.marginReport.query";

/*
 * OBJECTS
 */
const getSuccessfulCustomers = (data) =>
  "READ_SUCCESSFUL" === data?.CustomerFinancial?.status
    ? _.compact(data?.CustomerFinancial?.CustomerRead ?? [])
    : [];
const getTotalCount = (data) =>
  data?.CustomerFinancial?._totalCount ?? data?.CustomerFinancial?.totalCount ?? data?.CustomerFinancial?.CustomerRead?.length ?? 0;
const getCompanyKey = (row) => row.id;

const buildDestinationRows = (report) => {
  const totalsByDestination = {};

  for (const value of _.compact(Object.values(report ?? {}))) {
    for (const [destinationName, detail] of Object.entries(value ?? {})) {
      if (!totalsByDestination[destinationName]) {
        totalsByDestination[destinationName] = {
          destinationName: destinationName,
          revenue: 0,
          cost: 0,
          margin: 0,
        };
      }

      totalsByDestination[destinationName].revenue += Number.parseFloat(detail?.revenue ?? 0) || 0;
      totalsByDestination[destinationName].cost += Number.parseFloat(detail?.cost ?? 0) || 0;
      totalsByDestination[destinationName].margin += Number.parseFloat(detail?.margin ?? 0) || 0;
    }
  }

  return Object.values(totalsByDestination).sort((a, b) =>
    a.destinationName.localeCompare(b.destinationName),
  );
};

const buildCompanyMarginRow = (customer, marginReport) => {
  const destinations = buildDestinationRows(marginReport?._report);
  const marginTotals = marginReport?.marginTotals;
  const fallbackTotals = destinations.reduce(
    (acc, item) => ({
      revenue: acc.revenue + (Number.parseFloat(item.revenue ?? 0) || 0),
      cost: acc.cost + (Number.parseFloat(item.cost ?? 0) || 0),
      margin: acc.margin + (Number.parseFloat(item.margin ?? 0) || 0),
    }),
    { revenue: 0, cost: 0, margin: 0 },
  );

  return {
    id: customer?.id,
    companyName: customer?.Company?.displayName ?? "-",
    customerType: customer?.type ?? "-",
    currency: customer?.Billing?.currency,
    customers: customer?.id ? [customer] : [],
    totalRevenue: Number.parseFloat(marginTotals?.totalRevenue ?? fallbackTotals.revenue) || 0,
    totalCost: Number.parseFloat(marginTotals?.totalCost ?? fallbackTotals.cost) || 0,
    totalMargin: Number.parseFloat(marginTotals?.totalMargin ?? fallbackTotals.margin) || 0,
    destinations: destinations,
  };
};

const buildCompanyFinancialRow = (customer, index) => ({
  id: customer?.customerId ?? customer?.id ?? `financial-customer-${index}`,
  companyName: customer?.displayName ?? "-",
  customerType: customer?.type ?? "-",
  currency: customer?.currency,
  // Keep the old customer id shape so CustomerMarginReportQuery can run only when a row expands.
  customers:
    customer?.customerId || customer?.id
      ? [{ ...customer, id: customer?.customerId ?? customer?.id }]
      : [],
  totalRevenue: Number.parseFloat(customer?.totalRevenue ?? 0) || 0,
  totalCost: Number.parseFloat(customer?.totalCost ?? 0) || 0,
  totalMargin: Number.parseFloat(customer?.totalMargin ?? 0) || 0,
  hasFinancialData: Boolean((Number.parseFloat(customer?.totalRevenue ?? 0) || 0) || (Number.parseFloat(customer?.totalCost ?? 0) || 0) || (Number.parseFloat(customer?.totalMargin ?? 0) || 0)),
  destinations: [],
});

const groupCompanyRows = (rows) =>
  Object.values(
    rows.reduce((acc, row, index) => {
      const companyKey = getCompanyKey(row);

      if (!acc[companyKey]) {
        acc[companyKey] = {
          ...row,
          id: companyKey,
          customerType: row.customerType,
          totalRevenue: 0,
          totalCost: 0,
          totalMargin: 0,
          hasFinancialData: row.hasFinancialData,
          destinations: [],
          customers: [],
          destinationMap: {},
        };
      } else if (
        !String(acc[companyKey].customerType).includes(row.customerType)
      ) {
        acc[companyKey].customerType =
          `${acc[companyKey].customerType}, ${row.customerType}`;
      }

      acc[companyKey].totalRevenue += Number.parseFloat(row.totalRevenue ?? 0) || 0;
      acc[companyKey].totalCost += Number.parseFloat(row.totalCost ?? 0) || 0;
      acc[companyKey].totalMargin += Number.parseFloat(row.totalMargin ?? 0) || 0;
      acc[companyKey].customers.push(...(row.customers ?? []));

      for (const destination of row.destinations) {
        const existingDestination =
          acc[companyKey].destinationMap[destination.destinationName];

        if (existingDestination) {
          existingDestination.revenue += Number.parseFloat(destination.revenue ?? 0) || 0;
          existingDestination.cost += Number.parseFloat(destination.cost ?? 0) || 0;
          existingDestination.margin += Number.parseFloat(destination.margin ?? 0) || 0;
        } else {
          acc[companyKey].destinationMap[destination.destinationName] = {
            ...destination,
          };
          acc[companyKey].destinations.push(
            acc[companyKey].destinationMap[destination.destinationName],
          );
        }
      }

      return acc;
    }, {}),
  )
    .map(({ destinationMap, ...row }) => ({
      ...row,
      destinations: row.destinations.sort((a, b) =>
        a.destinationName.localeCompare(b.destinationName),
      ),
    }))
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

/*
 * COMPONENT
 */
const Index = () => {
  // Hook assignment.
  const [readCustomerMarginReport] = useLazyQuery(CustomerMarginReportQuery, {
    fetchPolicy: Object.React.App.fetchPolicy,
  });
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [companyRows, setCompanyRows] = React.useState([]);
  const [expandedRows, setExpandedRows] = React.useState({});
  const [loadingCompanyRows, setLoadingCompanyRows] = React.useState({});
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  
  const tableHeaderHeightRef = React.useRef(0);
  // Track latest request per company so slower responses cannot overwrite newer margin data.
  const activeRequestRef = React.useRef({});
  // Track loading in a ref to prevent duplicate background calls without rerendering every row.
  const loadingCompanyRowsRef = React.useRef({});
  // Keep expand state readable inside stable callbacks without rebuilding column renderers.
  const expandedRowsRef = React.useRef({});
  // Keep full company objects out of DataGrid row data to reduce row memory/render cost.
  const companyRowsByIdRef = React.useRef({});
  const isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  const customerFinancialVariables = React.useMemo(
    () => ({
      smsStartDate: dateRange.selection.startDate,
      smsEndDate: dateRange.selection.endDate,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    }),
    [
      dateRange.selection.endDate,
      dateRange.selection.startDate,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const isPreviousPageRef = React.useRef(false);
  const paginationModelRef = React.useRef(paginationModel);
  const rowCountRef = React.useRef(0);

  const customerFinancialQuery = useQuery(CustomerFinancialQuery, {
    variables: customerFinancialVariables,
    fetchPolicy: isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
  });
  const { refetch: refetchCustomers } = customerFinancialQuery;

  const customers = React.useMemo(
    () => getSuccessfulCustomers(customerFinancialQuery?.data),
    [customerFinancialQuery?.data],
  );
  const totalCountFromResponse = customerFinancialQuery?.data
    ? getTotalCount(customerFinancialQuery?.data)
    : void 0;
  const totalCount = React.useMemo(() => {
    if (undefined !== totalCountFromResponse && null !== totalCountFromResponse)
      rowCountRef.current = totalCountFromResponse;

    return rowCountRef.current;
  }, [totalCountFromResponse]);

 

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await refetchCustomers(customerFinancialVariables);
    } finally {
      setIsRefreshing(false);
    }
  }, [customerFinancialVariables, refetchCustomers]);
  const handlePaginationModelChange = React.useCallback(
    (nextPaginationModel) => {
      // Only backward pagination with the same page size should prefer cached rows.
      isPreviousPageRef.current =
        nextPaginationModel.page < paginationModelRef.current.page &&
        nextPaginationModel.pageSize === paginationModelRef.current.pageSize;

      paginationModelRef.current = nextPaginationModel;
      setPaginationModel(nextPaginationModel);
    },
    [],
  );

  const loadCompanyMarginDetails = React.useCallback(
    async (company) => {
      // Skip duplicate fetches once a company is loaded or already loading.
      if (company.isMarginLoaded || loadingCompanyRowsRef.current?.[company.id])
        return company;

      const requestId = (activeRequestRef.current?.[company.id] ?? 0) + 1;
      activeRequestRef.current = {
        ...activeRequestRef.current,
        [company.id]: requestId,
      };
      loadingCompanyRowsRef.current = {
        ...loadingCompanyRowsRef.current,
        [company.id]: true,
      };
      setLoadingCompanyRows((current) => ({ ...current, [company.id]: true }));

      try {
        const activeCustomer = _.first(company.customers ?? []);

        if (_.isEmpty(activeCustomer?.id)) {
          const nextCompany = {
            ...company,
            destinations: [],
            isMarginLoaded: true,
          };

          // Update the ref before React state commits so the first click can finish correctly.
          companyRowsByIdRef.current = {
            ...companyRowsByIdRef.current,
            [company.id]: nextCompany,
          };

          setCompanyRows((current) => {
            const nextRows = current.map((row) =>
              row.id === company.id ? nextCompany : row,
            );

            companyRowsByIdRef.current = Object.fromEntries(
              nextRows.map((row) => [row.id, row]),
            );

            return nextRows;
          });
          return nextCompany;
        }

        const { data } = await readCustomerMarginReport({
          variables: {
            customerId: activeCustomer.id,
            startDate: dateRange.selection.startDate,
            endDate: dateRange.selection.endDate,
          },
        }).catch(() => ({ data: {} }));

        // Re-check request freshness before writing async results into React state.
        if (activeRequestRef.current?.[company.id] !== requestId) return;

        const companyDetail = buildCompanyMarginRow(
          activeCustomer,
          data?.CustomerMarginReport,
        );

        const nextCompany = {
          ...company,
          totalRevenue: companyDetail?.totalRevenue ?? company.totalRevenue,
          totalCost: companyDetail?.totalCost ?? company.totalCost,
          totalMargin: companyDetail?.totalMargin ?? company.totalMargin,
          destinations: companyDetail?.destinations ?? [],
          isMarginLoaded: true,
        };

        // Update the ref before React state commits so the same click can expand after data loads.
        companyRowsByIdRef.current = {
          ...companyRowsByIdRef.current,
          [company.id]: nextCompany,
        };

        setCompanyRows((current) => {
          const nextRows = current.map((row) =>
            row.id === company.id ? nextCompany : row,
          );

          companyRowsByIdRef.current = Object.fromEntries(
            nextRows.map((row) => [row.id, row]),
          );

          return nextRows;
        });
        return nextCompany;
      } finally {
        loadingCompanyRowsRef.current = {
          ...loadingCompanyRowsRef.current,
          [company.id]: false,
        };
        setLoadingCompanyRows((current) => ({
          ...current,
          [company.id]: false,
        }));
      }
    },
    [
      dateRange.selection.endDate,
      dateRange.selection.startDate,
      readCustomerMarginReport,
    ],
  );

  React.useEffect(() => {
    const nextCompanyRows = groupCompanyRows(
      customers.map((customer, index) =>
        buildCompanyFinancialRow(customer, index),
      ),
    );

    setCompanyRows(nextCompanyRows);
    setExpandedRows({});
    setLoadingCompanyRows({});
    expandedRowsRef.current = {};
    loadingCompanyRowsRef.current = {};
    activeRequestRef.current = {};
    companyRowsByIdRef.current = Object.fromEntries(
      nextCompanyRows.map((company) => [company.id, company]),
    );
  }, [customers]);

  const handleToggleRow = React.useCallback(
    async (companyId) => {
      const company = companyRowsByIdRef.current?.[companyId];

      if (!company) return;

      let nextCompany = company;

      if (!company.isMarginLoaded) {
        nextCompany = await loadCompanyMarginDetails(company);
      }

      if (_.isEmpty(nextCompany?.destinations)) return;

      if (expandedRowsRef.current?.[company.id]) {
        expandedRowsRef.current = {
          ...expandedRowsRef.current,
          [company.id]: false,
        };
        setExpandedRows((current) => ({ ...current, [company.id]: false }));
        return;
      }

      expandedRowsRef.current = {
        ...expandedRowsRef.current,
        [company.id]: true,
      };
      setExpandedRows((current) => ({ ...current, [company.id]: true }));
    },
    [loadCompanyMarginDetails],
  );

  const renderSerialCell = React.useCallback(
    (params) => (
      <Flex align="center" width="100%" position="relative">
        <Flex position="absolute" left="0px" width="45px" justify="center">
          {params.row.isExpandable && (
            <IconButton
              size="xs"
              variant="ghost"
              icon={
                params.row.isLoading ? (
                  <Spinner size="xs" color="purple.500" />
                ) : params.row.isExpanded ? (
                  <ChevronDownIcon />
                ) : (
                  <ChevronRightIcon />
                )
              }
              onClick={(event) => {
                // Prevent DataGrid cell/row click handling from also firing.
                event.stopPropagation();
                if (!params.row.isLoading)
                  handleToggleRow(params.row.companyId);
              }}
              aria-label={params.row.isExpanded ? "Collapse" : "Expand"}
              isDisabled={params.row.isLoading}
              minW="20px"
              h="20px"
              _focus={{ boxShadow: "none" }}
              _active={{ bg: "transparent" }}
            />
          )}
        </Flex>
        <Flex width="100%" justify="center" align="center">
          {params.value}
        </Flex>
      </Flex>
    ),
    [handleToggleRow],
  );

  const columns = React.useMemo(
    () => [
      {
        field: "serialNo",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: renderSerialCell,
      },
      {
        field: "id",
        headerName: "ID",
        hide: true,
      },
      {
        field: "companyName",
        headerName: "Company Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 260,
      },
      {
        field: "totalCost",
        headerName: " Cost",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "totalRevenue",
        headerName: " Revenue",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "totalMargin",
        headerName: " Margin",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
    ],
    [renderSerialCell],
  );

  const formattedCompanyRows = React.useMemo(
    () =>
      // Keep formatting separate so expand/collapse only rebuilds row structure, not money values.
      companyRows.map((company, index) => ({
        ...company,
        serialNo: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
        totalMarginFormatted: (Number.parseFloat(company.totalMargin ?? 0) || 0).toFixed(2),
        totalCostFormatted: (Number.parseFloat(company.totalCost ?? 0) || 0).toFixed(2),
        totalRevenueFormatted: (Number.parseFloat(company.totalRevenue ?? 0) || 0).toFixed(2),
        destinations: company.destinations.map(
          (destination, destinationIndex) => ({
            ...destination,
            serialNo: String.fromCharCode(97 + destinationIndex),
            totalMarginFormatted: (Number.parseFloat(destination.margin ?? 0) || 0).toFixed(2),
            totalCostFormatted: (Number.parseFloat(destination.cost ?? 0) || 0).toFixed(2),
            totalRevenueFormatted: (Number.parseFloat(destination.revenue ?? 0) || 0).toFixed(2),
          }),
        ),
      })),
    [companyRows, paginationModel.page, paginationModel.pageSize],
  );

  const rows = React.useMemo(() => {
    const nextRows = [];

    formattedCompanyRows.forEach((company) => {
      const isExpanded = Boolean(expandedRows?.[company.id]);

      nextRows.push({
        id: `company-${company.id}`,
        serialNo: company.serialNo,
        companyName: company.companyName,
        totalMargin: company.totalMarginFormatted,
        totalCost: company.totalCostFormatted,
        totalRevenue: company.totalRevenueFormatted,
        isCompany: true,
        // Show expand only when CustomerFinancial has totals; empty rows should not trigger margin API.
        isExpandable:
          company.hasFinancialData &&
          (!company.isMarginLoaded || !_.isEmpty(company.destinations)),
        isExpanded: isExpanded,
        isLoading: Boolean(loadingCompanyRows?.[company.id]),
        companyId: company.id,
        groupKey: company.id,
      });

      if (isExpanded)
        company.destinations.forEach((destination) => {
          nextRows.push({
            id: `company-${company.id}-destination-${destination.destinationName}`,
            serialNo: destination.serialNo,
            companyName: destination.destinationName,
            totalMargin: destination.totalMarginFormatted,
            totalCost: destination.totalCostFormatted,
            totalRevenue: destination.totalRevenueFormatted,
            isCompany: false,
            groupKey: company.id,
          });
        });
    });

    return nextRows;
  }, [expandedRows, formattedCompanyRows, loadingCompanyRows]);

  const gridSlots = React.useMemo(
    () => ({
      loadingOverlay: () => (
        <TableSpinner
          isLoading={true}
          isEmpty={false}
          chopHeightFromHundredPercentage={
            tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
      noResultsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
      noRowsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
    }),
    [],
  );

  const isLoading = customerFinancialQuery.loading;

  // Return component.
  return (
    <Flex className="reportsCompanyMargin base">
      <Flex
        display="flex"
        flex={1}
        flexDirection="column"
        gap={isCurrentViewMobile ? "12px" : "22px"}
        bg="white"
        minH="83vh"
        maxH="83vh"
        overflow="hidden"
        borderRadius="20px"
        p={isCurrentViewMobile ? "12px" : "22px"}
      >
        <Flex
          pb="0px"
          justifyContent="space-between"
          alignItems={isCurrentViewMobile ? "stretch" : "center"}
          gap="12px"
          flexWrap="wrap"
        >
          <Flex gap="12px" flexDir="column">
            <Text
              as="h3"
              fontSize={17}
              fontWeight={500}
              display="flex"
              alignItems="center"
              color="brand.500"
              gap="6px"
            >
              <HiArrowUturnDown />
              {isCurrentViewMobile
                ? "Filter Rows"
                : "Filter Rows based on Date"}
              .
            </Text>
            <DateRangePicker
              startDate={dateRange?.selection?.startDate}
              endDate={dateRange?.selection?.endDate}
              w="100%"
              bg="purple.50"
              color="purple.600"
              fontSize="13px"
              height="max-content"
              gap="6px"
              setSelectDate={setDateRange}
            />
          </Flex>
          <Button
            borderRadius="8px"
            fontWeight="450"
            size="sm"
            minW="20px"
            backgroundColor="purple.200"
            color="purple"
            _hover={{ bg: "purple.800", color: "white" }}
            leftIcon={<HiArrowPath />}
            isDisabled={isRefreshing}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Flex>

        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={48}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 30, 50, 100]}
          slots={gridSlots}
          sx={{ flex: 1, minHeight: 0 }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          loading={isLoading}
        />
      </Flex>
    </Flex>
  );
};

/*
 * EXPORT
 */
export default Index;
